import { Box } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as documentService from "../../../models/services/documentService";
import { ISessionState } from "../../../models/session/sessionState";
import { AddButton } from "../../framework/Button/AddButton";
import { List } from "../../framework/List/List";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { PageTitle } from "../../framework/Page/PageTitle";
import { DocumentListItem, IDocumentListItem } from "source/models/document/documentListItem";
import { PreviewToggleButton } from "source/components/framework/Button/PreviewToggleButton";
import { SettingsButton } from "source/components/framework/Button/SettingsButton";
import { ConfirmationDialogResult, FileType, Permission } from "ui/Scripts/source/models/enums";
import { DocumentListDetailsPanel } from "../DocumentListDetailsPanel";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { SaveDataObj } from "source/framework/saveData";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { DocumentRemoveItem } from "source/models/document/documentRemoveItem";
import { DocumentFileEditDialog } from "../DocumentFileEditDialog/DocumentFileEditDialog";
import { DocumentCopyMoveDialog } from "../DocumentCopyMoveDialog/DocumentCopyMoveDialog";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { IFolderTreeNode } from "source/models/document/folderTreeNode";
import { DocumentDocumentEditDialog } from "../DocumentDocumentEditDialog";
import { DocumentDocumentNewDialog } from "../DocumentDocumentNewDialog";
import { EditToggleButtonHeader } from "source/components/framework/Button/EditToggleButtonHeader";
import { PageListActionContainer } from "source/components/framework/List/PageListActionContainer";
import { ListActionButtons } from "source/components/framework/List/ListActionButtons";
import { ListSearchContainer } from "source/components/framework/List/ListSearchContainer";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { DocumentPageDashboardChart, IDocumentGeneralFilter, IDocumentPageFilterState } from "../DocumentPageDashboardChart";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { AppAddIcon } from "source/components/framework/Icon/AppAddIcon";
import { AppUploadIcon } from "source/components/framework/Icon/AppUploadIcon";
import { DocumentListLineFirstColumn } from "./DocumentListLineFirstColumn";

export interface IDocumentListProps extends IDocumentPageFilterState {
    sessionState: ISessionState;
    canEditProperties: boolean;
    companies: ICompanyMenuItem[];
    companyId: string;
    editMode: boolean;
    filter: string;
    listFilterIsSet: boolean;
    folderTree: IFolderTreeNode;
    foldersLoading: boolean;
    folderId: string;
    folderComment: string;
    folderPermission: Permission;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    showDocumentPreview: boolean;
    showPublishedVersionOnDocumentPreview: boolean;
    settingsVisible: boolean;
    reportTabs?: React.ReactNode;
    onShowSettingsDialog: () => void;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onChangeShowDocumentPreview?: (showDocumentPreview: boolean) => void;
    onChangeShowPublishedVersionOnDocumentPreview?: (showPublishedVersionOnDocumentPreview: boolean) => void;
    onChangeEditMode?: (editMode: boolean) => void;
    onChangeFilter: (filter: string) => void;
    onOpenFiltersPanel: () => void;
}

interface IDocumentListSearchFilter extends IDocumentGeneralFilter {
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
}

interface IDocumentListState {
    // List
    intialized: boolean;
    isLoading: boolean;
    items: IDocumentListItem[];
    selectedId: string;
    checkedIds: string[];
    canDownloadSelectedDocuments: boolean;
    canPrintSelectedDocuments: boolean;
    selectedItem: IDocumentListItem;

    // Preview
    previewCompanyId: string;
    previewDataId: string;

    // Dialog: File
    showFileEditDialog: boolean;
    fileEditDialogId: string;
    fileEditDialogHasPrevious: boolean;
    fileEditDialogHasNext: boolean;

    // Dialog: Document New
    showDocumentnewDialog: boolean;

    // Dialog: Document
    showDocumentEditDialog: boolean;
    documentEditDialogId: string;
    documentEditDialogContent: string;
    documentEditDialogLandscapeOrientation: boolean;
    documentEditDialogHasPrevious: boolean;
    documentEditDialogHasNext: boolean;

    // Dialog: Copy/Move
    showCopyMoveDialog: boolean;
    copyMoveDialogItems: IDocumentListItem[];
}

export class DocumentList extends React.Component<IDocumentListProps, IDocumentListState> {
    sendReadReceipts: string[] = [];

    constructor(props: IDocumentListProps) {
        super(props);
        this.state = {
            intialized: false,
            isLoading: false,
            items: [],
            selectedId: null,
            checkedIds: [],
            canDownloadSelectedDocuments: false,
            canPrintSelectedDocuments: false,
            selectedItem: null,
            // Preview
            previewDataId: "",
            previewCompanyId: "",
            // Dialog: File
            showFileEditDialog: false,
            fileEditDialogId: "",
            fileEditDialogHasPrevious: false,
            fileEditDialogHasNext: false,
            // Dialog: Document New
            showDocumentnewDialog: false,
            // Dialog: Document
            showDocumentEditDialog: false,
            documentEditDialogId: "",
            documentEditDialogContent: "",
            documentEditDialogLandscapeOrientation: false,
            documentEditDialogHasPrevious: false,
            documentEditDialogHasNext: false,
            // Dialog: Copy/Move
            showCopyMoveDialog: false,
            copyMoveDialogItems: []
        };
    }

    static getSelectedState = (items: IDocumentListItem[], selectedId: string, checkedIds: string[]) => {
        const selectedIds = List.getSelectedIds(selectedId, checkedIds);
        const selectedItems = items.filter(i => selectedIds.indexOf(i.id) > -1);
        return {
            selectedId,
            checkedIds,
            canDownloadSelectedDocuments: !!selectedItems.find(i => i.fileType !== FileType.Document || !!i.publishedId),
            canPrintSelectedDocuments: !!selectedItems.find(i => i.fileType === FileType.Document),
            selectedItem: selectedId ? items.find(i => i.id === selectedId) : null
        };
    };

    loadDocuments = async (setDialogData: boolean = true, newSelectedId: string = null, closeDocumentEditDialog: boolean = false) => {
        const props = this.props;
        if (!props.companyId || !props.folderId || props.foldersLoading) return;
        this.setState({ isLoading: true });
        await AppUtils.callService(() => documentService.getDocumentListItems(props.companyId, props.folderId), false, true, (result) => {
            if (!result) {
                this.setState({ isLoading: false });
                return;
            }
            const state = this.state;
            const items = DocumentListItem.sortDocumentListItems(result.items, props.sortColumn, props.sortOrderIsAsc);
            const currentSelectedId = newSelectedId ?? state.selectedId;
            const selectedId = items.find(i => i.id === currentSelectedId)
                ? currentSelectedId
                : (items.length > 0 ? items[0].id : "");
            const checkedIds = Base.keepAvailableSelectedIds(items, state.checkedIds);
            const fileItems = items.filter(i => i.fileType !== FileType.Document);
            const fileIndex = selectedId ? fileItems.findIndex(i => i.id === selectedId) : -1;
            const fileSelectedItem = fileIndex > -1 ? fileItems[fileIndex] : null;
            const documentItems = items.filter(i => i.fileType === FileType.Document);
            const documentIndex = selectedId ? documentItems.findIndex(i => i.id === selectedId) : -1;
            const documentSelectedItem = documentIndex > -1 ? documentItems[documentIndex] : null;
            const selectedState = DocumentList.getSelectedState(items, selectedId, checkedIds);
            this.setState({
                ...selectedState,
                isLoading: false,
                intialized: true,
                items,
                previewCompanyId: props.companyId,
                fileEditDialogId: setDialogData ? (fileSelectedItem ? fileSelectedItem.id : "") : state.fileEditDialogId,
                fileEditDialogHasPrevious: setDialogData ? fileIndex > 0 : state.fileEditDialogHasPrevious,
                fileEditDialogHasNext: setDialogData ? (fileIndex < items.length - 1) : state.fileEditDialogHasNext,
                documentEditDialogId: setDialogData ? (documentSelectedItem ? documentSelectedItem.id : "") : state.documentEditDialogId,
                documentEditDialogHasPrevious: setDialogData ? documentIndex > 0 : state.documentEditDialogHasPrevious,
                documentEditDialogHasNext: setDialogData ? (documentIndex < items.length - 1) : state.documentEditDialogHasNext,
                showDocumentEditDialog: closeDocumentEditDialog ? false : state.showDocumentEditDialog
            });
        });
    };

    searchDocuments = async (searchFilter: IDocumentListSearchFilter, setDialogData: boolean = true, newSelectedId: string = null, closeDocumentEditDialog: boolean = false) => {
        const props = this.props;
        if (!props.companyId || !props.folderId || props.foldersLoading) return;
        this.setState({ isLoading: true });
        await AppUtils.callService(() => documentService.searchDocumentListItems(props.companyId, props.folderId, searchFilter.filter,
            searchFilter.startDate, searchFilter.endDate, searchFilter.dynamicTimeFilterId,
            searchFilter.documentEndDateStartDate, searchFilter.documentEndDateEndDate, searchFilter.dynamicDocumentEndDateFilterId,
            searchFilter.keywordIds, searchFilter.keywordOperatorId, searchFilter.documentApprovalId, searchFilter.documentContentSearchId,
            searchFilter.maxResultsCount, searchFilter.sortColumn, searchFilter.sortOrderIsAsc), false, true, (result) => {
            if (!result) {
                this.setState({ isLoading: false });
                return;
            }
            const state = this.state;
            const items = DocumentListItem.sortDocumentListItems(result.items, props.sortColumn, props.sortOrderIsAsc);
            const currentSelectedId = newSelectedId ?? state.selectedId;
            const selectedId = items.find(i => i.id === currentSelectedId)
                ? currentSelectedId
                : (items.length > 0 ? items[0].id : "");
            const checkedIds = Base.keepAvailableSelectedIds(items, state.checkedIds);
            const fileItems = items.filter(i => i.fileType !== FileType.Document);
            const fileIndex = selectedId ? fileItems.findIndex(i => i.id === selectedId) : -1;
            const fileSelectedItem = fileIndex > -1 ? fileItems[fileIndex] : null;
            const documentItems = items.filter(i => i.fileType === FileType.Document);
            const documentIndex = selectedId ? documentItems.findIndex(i => i.id === selectedId) : -1;
            const documentSelectedItem = documentIndex > -1 ? documentItems[documentIndex] : null;
            const selectedState = DocumentList.getSelectedState(items, selectedId, checkedIds);
            this.setState({
                ...selectedState,
                isLoading: false,
                intialized: true,
                items,
                previewCompanyId: props.companyId,
                fileEditDialogId: setDialogData ? (fileSelectedItem ? fileSelectedItem.id : "") : state.fileEditDialogId,
                fileEditDialogHasPrevious: setDialogData ? fileIndex > 0 : state.fileEditDialogHasPrevious,
                fileEditDialogHasNext: setDialogData ? (fileIndex < items.length - 1) : state.fileEditDialogHasNext,
                documentEditDialogId: setDialogData ? (documentSelectedItem ? documentSelectedItem.id : "") : state.documentEditDialogId,
                documentEditDialogHasPrevious: setDialogData ? documentIndex > 0 : state.documentEditDialogHasPrevious,
                documentEditDialogHasNext: setDialogData ? (documentIndex < items.length - 1) : state.documentEditDialogHasNext,
                showDocumentEditDialog: closeDocumentEditDialog ? false : state.showDocumentEditDialog
            });
        });
    };

    updateList = async (setDialogData: boolean = true, newSelectedId: string = null, closeDocumentEditDialog: boolean = false) => {
        const props = this.props;
        if (!props.listFilterIsSet && !props.filter) {
            await this.loadDocuments(setDialogData, newSelectedId, closeDocumentEditDialog);
        } else {
            await this.searchDocuments({ ...DocumentPageDashboardChart.getGeneralFilterFromState(props), sortColumn: props.sortColumn, sortOrderIsAsc: props.sortOrderIsAsc, filter: props.filter }, setDialogData, newSelectedId, closeDocumentEditDialog);
        }
    };

    componentDidMount(): void {
        this.updateList();
    }

    componentDidUpdate(prevProps: IDocumentListProps, prevState: IDocumentListState) {
        const props = this.props;
        const prevFoldersLoading = prevProps.foldersLoading ? prevProps.foldersLoading : false;
        const foldersLoading = props.foldersLoading ? props.foldersLoading : false;
        if (foldersLoading) return;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const prevFolderId = prevProps.folderId ? prevProps.folderId : "";
        const folderId = props.folderId ? props.folderId : "";
        const prevListFilterIsSet = prevProps.listFilterIsSet ? prevProps.listFilterIsSet : false;
        const listFilterIsSet = props.listFilterIsSet ? props.listFilterIsSet : false;
        const filterStateChange = DocumentPageDashboardChart.getDocumentGeneralFilterChange(prevProps, props);
        const prevFilter = prevProps.filter ? prevProps.filter : "";
        const filter = props.filter ? props.filter : "";
        if (companyId === prevCompanyId && folderId === prevFolderId && foldersLoading === prevFoldersLoading && listFilterIsSet === prevListFilterIsSet && filter === prevFilter && filterStateChange.notChanged) {
            const prevSortColumn = prevProps.sortColumn ? prevProps.sortColumn : "";
            const sortColumn = props.sortColumn ? props.sortColumn : "";
            const prevSortOrderIsAsc = prevProps.sortOrderIsAsc ?? false;
            const sortOrderIsAsc = props.sortOrderIsAsc ?? false;
            if (sortColumn === prevSortColumn && sortOrderIsAsc === prevSortOrderIsAsc) return;
            this.setState({
                items: DocumentListItem.sortDocumentListItems(this.state.items, sortColumn, sortOrderIsAsc)
            });
            return;
        }
        if (!listFilterIsSet && !props.filter) {
            this.loadDocuments();
        } else {
            this.searchDocuments({ ...filterStateChange, sortColumn: props.sortColumn, sortOrderIsAsc: props.sortOrderIsAsc, filter: props.filter });
        }
    }

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        const selectedState = DocumentList.getSelectedState(this.state.items, selectedId, checkedIds);
        this.setState(selectedState);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        this.props.onSetSortData(sortColumn, sortOrderIsAsc);
    };

    refreshList = async (setDialogData: boolean = true, newSelectedId: string = null, forcePreviewUpdate: boolean = true, closeDocumentEditDialog: boolean = false) => {
        await this.updateList(setDialogData, newSelectedId, closeDocumentEditDialog);
        if (!forcePreviewUpdate) return;
        this.setState({ previewDataId: Base.getGuid() });
    };

    // #region File Edit dialog
    handleShowFileEditDialog = (id: string) => {
        const state = this.state;
        const isNew = id === Base.emptyGuid;
        const fileItems = state.items.filter(i => i.fileType !== FileType.Document);
        const index = isNew
            ? 0
            : fileItems.findIndex(i => i.id === id);
        if (index < 0) return;
        const selectedState = DocumentList.getSelectedState(state.items, isNew ? state.selectedId : fileItems[index].id, state.checkedIds);
        this.setState({
            ...selectedState,
            showFileEditDialog: true,
            fileEditDialogId: id,
            fileEditDialogHasPrevious: index > 0,
            fileEditDialogHasNext: isNew
                ? false
                : index < fileItems.length - 1
        });
    };

    handleFileEditDialogOk = () => {
        this.refreshList(false);
    };

    handleFileEditDialogCancel = () => {
        this.setState({
            showFileEditDialog: false
        });
        this.refreshList();
    };

    setFileEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        if (index < 0) return;
        const fileItems = state.items.filter(i => i.fileType !== FileType.Document);
        if (index >= fileItems.length) return;
        const selectedState = DocumentList.getSelectedState(state.items, fileItems[index].id, state.checkedIds);
        this.setState({
            ...selectedState,
            fileEditDialogId: fileItems[index].id,
            fileEditDialogHasPrevious: index > 0,
            fileEditDialogHasNext: index < fileItems.length - 1
        });
    };

    handleFileEditDialogMoveToPrevious = () => {
        const state = this.state;
        if (!state.fileEditDialogId) return;
        const index = state.items.filter(i => i.fileType !== FileType.Document).findIndex(i => i.id === state.fileEditDialogId);
        if (index < 0) return;
        this.setFileEditDialogItemByIndex(index - 1);
    };

    handleFileEditDialogMoveToNext = () => {
        const state = this.state;
        if (!state.fileEditDialogId) return;
        const index = state.items.filter(i => i.fileType !== FileType.Document).findIndex(i => i.id === state.fileEditDialogId);
        if (index < 0) return;
        this.setFileEditDialogItemByIndex(index + 1);
    };
    // #endregion File Edit dialog

    // #region Document Edit dialog
    handleShowDocumentEditDialog = (id: string, content: string = null, landscaperOrientation: boolean = false) => {
        const state = this.state;
        const isNew = id === Base.emptyGuid;
        const documentItems = state.items.filter(i => i.fileType === FileType.Document);
        const index = isNew
            ? 0
            : documentItems.findIndex(i => i.id === id);
        if (index < 0) return;
        const selectedState = DocumentList.getSelectedState(state.items, isNew ? state.selectedId : documentItems[index].id, state.checkedIds);
        this.setState({
            ...selectedState,
            showDocumentEditDialog: true,
            documentEditDialogId: id,
            documentEditDialogContent: content,
            documentEditDialogLandscapeOrientation: landscaperOrientation,
            documentEditDialogHasPrevious: index > 0,
            documentEditDialogHasNext: isNew
                ? false
                : index < documentItems.length - 1
        });
    };

    handleDocumentEditDialogOk = (documentId: string, closeDialog: boolean) => {
        const oldDocumentEditDialogId = this.state.documentEditDialogId;
        this.refreshList(!closeDialog, documentId, closeDialog || oldDocumentEditDialogId !== Base.emptyGuid, closeDialog);
    };

    handleDocumentEditDialogCancel = () => {
        this.refreshList(false, null, true, true);
    };

    setDocumentEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        if (index < 0) return;
        const documentItems = state.items.filter(i => i.fileType === FileType.Document);
        if (index >= documentItems.length) return;
        const selectedState = DocumentList.getSelectedState(state.items, documentItems[index].id, state.checkedIds);
        this.setState({
            ...selectedState,
            documentEditDialogId: documentItems[index].id,
            documentEditDialogHasPrevious: index > 0,
            documentEditDialogHasNext: index < documentItems.length - 1
        });
    };

    handleDocumentEditDialogMoveToPrevious = () => {
        const state = this.state;
        if (!state.documentEditDialogId) return;
        const index = state.items.filter(i => i.fileType === FileType.Document).findIndex(i => i.id === state.documentEditDialogId);
        if (index < 0) return;
        this.setDocumentEditDialogItemByIndex(index - 1);
    };

    handleDocumentEditDialogMoveToNext = () => {
        const state = this.state;
        if (!state.documentEditDialogId) return;
        const index = state.items.filter(i => i.fileType === FileType.Document).findIndex(i => i.id === state.documentEditDialogId);
        if (index < 0) return;
        this.setDocumentEditDialogItemByIndex(index + 1);
    };
    // #endregion Document Edit dialog

    // #region Document New dialog
    handleAddEditableDocument = () => {
        this.setState({
            showDocumentnewDialog: true
        });
    };

    handleDocumentNewDialogOk = (content: string, landscaperOrientation: boolean) => {
        this.setState({
            showDocumentnewDialog: false
        });
        this.handleShowDocumentEditDialog(Base.emptyGuid, content, landscaperOrientation);
    };

    handleDocumentNewDialogCancel = () => {
        this.setState({
            showDocumentnewDialog: false
        });
    };
    // #endregion Document New dialog

    getAddAttachmentsSaveData = async (attachments: IFileAttachment[], overwrite: boolean): Promise<SaveDataObj> => {
        const props = this.props;
        const result = new SaveDataObj();
        result.append("id", props.folderId);
        result.append("companyId", props.companyId);
        result.append("overwrite", overwrite);
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(attachments));
        return result;
    };

    getCheckAttachmentsSaveData = (fileNames: string[]): SaveDataObj => {
        const props = this.props;
        const result = new SaveDataObj();
        result.append("id", props.folderId);
        result.append("companyId", props.companyId);
        result.append("fileNames", fileNames);
        return result;
    };

    uploadFiles = async (attachments: FileAttachment[], overwrite: boolean) => {
        const saveData = await this.getAddAttachmentsSaveData(attachments, overwrite);
        const result = await AppUtils.callService(() => documentService.uploadFiles(saveData.obj));
        if (!result) return;
        await this.updateList();
        //Update preview if needed
        if (!this.props.showDocumentPreview) return;
        const uploadedFileNames = attachments.map(i => i.name);
        const selectedItem = this.state.selectedItem;
        if (!selectedItem || !uploadedFileNames.find(i => i.equalIgnoreCase(selectedItem.code))) return;
        this.setState({ previewDataId: Base.getGuid() });
    };

    handleUploadFiles = async (fileList: FileList) => {
        let attachments = await FileAttachment.addFileListToFileAttachments([], fileList, 0);
        if (!attachments.length) return;
        //Check upload
        const saveData = this.getCheckAttachmentsSaveData(attachments.map(i => i.name));
        const result = await AppUtils.callService(() => documentService.uploadFilesCheck(saveData.obj), false);
        if (!result) return;
        //Upload
        if (!result.fileNamesInUse.length) {
            await this.uploadFiles(attachments, false);
            return;
        }
        const overwriteConfirmation = await AppUtils.showConfirmationDialog(String.format(result.fileNamesInUse.length > 1
            ? Translations.DocumentsParameterOverwriteConfirmation
            : Translations.DocumentParameterOverwriteConfirmation, result.fileNamesInUse.join(", ")), true);
        if (overwriteConfirmation === ConfirmationDialogResult.Cancel) return;
        if (overwriteConfirmation === ConfirmationDialogResult.No) {
            attachments = attachments.filter(i => !result.fileNamesInUse.find(j => j === i.name));
            if (!attachments.length) return;
        }
        await this.uploadFiles(attachments, overwriteConfirmation === ConfirmationDialogResult.Yes);
    };

    handleOpenDocument = (id: string) => {
        if (!id || !this.props.editMode) return;
        const item = this.state.items.find(i => i.id === id);
        if (!item) return;
        if (item.fileType !== FileType.Document) {
            this.handleShowFileEditDialog(id);
        } else {
            this.handleShowDocumentEditDialog(id);
        }
    };

    handleOpenSelectedDocument = () => {
        const state = this.state;
        if (!state.selectedId) return;
        this.handleOpenDocument(state.selectedId);
    };

    // #region Copy/Move
    handleCopyMoveSelectedDocuments = () => {
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        this.setState({
            showCopyMoveDialog: true,
            copyMoveDialogItems: state.items.filter(i => selectedIds.indexOf(i.id) > -1)
        });
    };

    handleCopyMoveDialogOk = (refreshDocuments: boolean) => {
        this.setState({
            showCopyMoveDialog: false
        });
        if (!refreshDocuments) return;
        this.updateList();
    };
    
    handleCopyMoveDialogCancel = () => {
        this.setState({
            showCopyMoveDialog: false
        });
    };
    // #endregion Copy/Move

    handleDownloadDocument = async (id: string) => {
        if (!id) return;
        const props = this.props;
        const fileName = await AppUtils.callService(() => documentService.downloadFile(props.companyId, id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.DocumentParameterDownloaded, fileName));
    };

    handleDownloadSelectedDocuments = async () => {
        const props = this.props;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        const directoryItemIds = state.items.filter(i => selectedIds.indexOf(i.id) > -1 && (i.fileType !== FileType.Document || i.publishedId)).map(i => i.fileType !== FileType.Document ? i.id : i.publishedId);
        if (directoryItemIds.length < 1) return;
        if (directoryItemIds.length < 2) {
            await this.handleDownloadDocument(directoryItemIds[0]);
        } else {
            const fileName = await AppUtils.callService(() => documentService.downloadMultipleFiles(props.companyId, directoryItemIds));
            if (!fileName) return;
            AppUtils.showSuccessMessage(String.format(Translations.DocumentParameterDownloaded, fileName));
        }
    };

    handlePrintSelectedDocuments = async () => {
        const props = this.props;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        const selectedDocuments = state.items.filter(i => selectedIds.indexOf(i.id) > -1 && i.fileType === FileType.Document);
        if (!selectedDocuments.length) return;
        const fileName = await AppUtils.callService(() => documentService.printDocuments(props.companyId, selectedDocuments.map(i => i.id)));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    };

    handleDeleteSelectedDocuments = async () => {
        const props = this.props;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const deleteItems = state.items.filter(i => selectedIds.indexOf(i.id) > -1).map(i => new DocumentRemoveItem(i));
        if (await AppUtils.showConfirmationDialog(deleteItems.length < 2
            ? String.format(Translations.DoYouReallyWantToRemoveDocumentParameter, deleteItems[0].code)
            : String.format(Translations.DoYouReallyWantToRemoveDocumentsParameter, deleteItems.length.toString(10)), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await AppUtils.callService(() => documentService.removeDocuments(props.companyId, props.folderId, deleteItems), true);
            if (!result) return;
            this.updateList();
        }
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <div style={{ width: "100%", position: "relative", flexBasis: 0, flexGrow: 6, display: "flex" }}>
                <div style={{ width: "100%", position: "relative", flexBasis: 0, flexGrow: 3, display: "flex" }}>
                    <PageTitle title={Translations.Documents + (props.listFilterIsSet || props.filter ? " - " + Translations.Filtered : "")} subTitle={props.folderComment} position="absolute">
                        {props.settingsVisible &&
                            <SettingsButton onClick={props.onShowSettingsDialog} sx={{ marginLeft: "24px" }}>
                                {Translations.Settings}
                            </SettingsButton>
                        }
                        {props.editMode && props.folderPermission > Permission.Read &&
                            <AddButton
                                onClick={this.handleAddEditableDocument}
                                style={{ marginLeft: "24px" }}
                                actions={[
                                    { title: Translations.AddEditableDocument, icon: <AppAddIcon />, onClick: this.handleAddEditableDocument },
                                    { title: Translations.UploadDocument, icon: <AppUploadIcon />, onClick: null, onAddFilesClick: this.handleUploadFiles }
                                ]}>
                                {Translations.Add}
                            </AddButton>
                        }
                        {props.folderPermission > Permission.Read &&
                            <EditToggleButtonHeader onClick={() => props.onChangeEditMode(!props.editMode)} sx={{ marginLeft: "1.5rem" }} selected={props.editMode}>
                            </EditToggleButtonHeader>
                        }
                        <PreviewToggleButton value="preview" selected={props.showDocumentPreview} onChange={() => props.onChangeShowDocumentPreview(!props.showDocumentPreview)} style={{ marginLeft: "24px", padding: "7px" }} />
                    </PageTitle>
                    <MainListContainer sx={{ width: "100%", maxWidth: props.showDocumentPreview ? "calc((100vw - 192px - 350px - 48px) / 2)" : "calc(100vw - 192px - 350px - 48px)" }}>
                        <PageListActionContainer
                            leftItems={
                                <ListSearchContainer sx={{ margin: 0 }}>
                                    <ListSearchFilter
                                        searchFilter={props.filter}
                                        onSearchClick={props.onChangeFilter}
                                    />
                                </ListSearchContainer>
                                //props.reportTabs
                            }
                            rightItems={
                                <>
                                    <ListActionButtons
                                        filterIsSet={props.listFilterIsSet}
                                        onFilter={props.onOpenFiltersPanel}
                                    />
                                </>
                            }
                        />
                        <List
                            intialized={state.intialized}
                            isLoading={state.isLoading}
                            hasMore={false}
                            multiSelect={true}
                            globalLoadingInUse={false}
                            height="calc(100vh - 236px)" //Main List
                            header={{
                                columns: [
                                    { size: props.showDocumentPreview ? 9 : 10, column: "name", content: Translations.Name },
                                    { size: props.showDocumentPreview ? 3 : 2, column: "modifyDate", content: Translations.ModifyDate },
                                ]
                            }}
                            lines={state.items.map((item) => {
                                const documentStyle = item.fileType === FileType.Document && !item.publishedId ? { opacity: "0.65" } : null;
                                return {
                                    columns: [
                                        {
                                            size: props.showDocumentPreview ? 9 : 10,
                                            style: { position: "relative" },
                                            content: <DocumentListLineFirstColumn
                                                item={item}
                                                documentStyle={documentStyle}
                                                isSelected={state.selectedId === item.id}
                                                editMode={props.editMode}
                                                folderPermission={props.folderPermission}
                                                canDownloadSelectedDocuments={state.canDownloadSelectedDocuments}
                                                canPrintSelectedDocuments={state.canPrintSelectedDocuments}
                                                onCopyMoveSelectedDocuments={this.handleCopyMoveSelectedDocuments}
                                                onOpenOpenDocument={this.handleOpenDocument}
                                                onOpenSelectedDocument={this.handleOpenSelectedDocument}
                                                onDownloadSelectedDocuments={this.handleDownloadSelectedDocuments}
                                                onPrintSelectedDocuments={this.handlePrintSelectedDocuments}
                                                onDeleteSelectedDocuments={this.handleDeleteSelectedDocuments}
                                            />
                                        },
                                        {
                                            size: props.showDocumentPreview ? 3 : 2,
                                            content: <Box sx={documentStyle}>{Base.timeToDateStr(item.modifyUser.date)}</Box>
                                        }
                                    ],
                                    id: item.id
                                };
                            })}
                            sortColumn={props.sortColumn}
                            sortOrderIsAsc={props.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                            onOpenLine={this.handleOpenDocument}
                        />
                    </MainListContainer>
                </div>
                {state.showFileEditDialog &&
                    <DocumentFileEditDialog
                        companyId={props.companyId}
                        folderId={props.folderId}
                        sessionState={props.sessionState}
                        id={state.fileEditDialogId}
                        hasPrevious={state.fileEditDialogHasPrevious}
                        hasNext={state.fileEditDialogHasNext}
                        onSaved={this.handleFileEditDialogOk}
                        onClose={this.handleFileEditDialogCancel}
                        onRefreshList={() => this.refreshList(false)}
                        onPrevious={this.handleFileEditDialogMoveToPrevious}
                        onNext={this.handleFileEditDialogMoveToNext}
                    />
                }
                {state.showDocumentnewDialog &&
                    <DocumentDocumentNewDialog
                        companyId={props.companyId}
                        folderId={props.folderId}
                        onOk={this.handleDocumentNewDialogOk}
                        onCancel={this.handleDocumentNewDialogCancel}
                    />
                }
                {state.showDocumentEditDialog &&
                    <DocumentDocumentEditDialog
                        sessionState={props.sessionState}
                        canEditProperties={props.canEditProperties}
                        companyId={props.companyId}
                        folderId={props.folderId}
                        id={state.documentEditDialogId}
                        content={state.documentEditDialogContent}
                        landscapeOrientation={state.documentEditDialogLandscapeOrientation}
                        hasPrevious={state.documentEditDialogHasPrevious}
                        hasNext={state.documentEditDialogHasNext}
                        onOk={this.handleDocumentEditDialogOk}
                        onCancel={this.handleDocumentEditDialogCancel}
                        onRefreshList={() => this.refreshList(false)}
                        onPrevious={this.handleDocumentEditDialogMoveToPrevious}
                        onNext={this.handleDocumentEditDialogMoveToNext}
                    />
                }
                {props.showDocumentPreview &&
                    <DocumentListDetailsPanel
                        documentsLoading={state.isLoading}
                        showPublishedVersion={props.showPublishedVersionOnDocumentPreview}
                        sessionState={props.sessionState}
                        companyId={state.previewCompanyId}
                        documentId={state.selectedId}
                        dataId={state.previewDataId}
                        editDialogVisible={state.showDocumentEditDialog}
                        documentVersionId={state.selectedItem?.documentVersionId}
                        documentFileType={state.selectedItem?.fileType}
                        publishedId={state.selectedItem?.publishedId}
                        onDownloadDocument={this.handleDownloadDocument}
                        onChangeShowPublishedVersion={props.onChangeShowPublishedVersionOnDocumentPreview}
                        onClose={() => props.onChangeShowDocumentPreview(false)}
                    />
                }
                {state.showCopyMoveDialog &&
                    <DocumentCopyMoveDialog
                        sessionState={props.sessionState}
                        companies={props.companies}
                        companyId={props.companyId}
                        folderId={props.folderId}
                        folderPermission={props.folderPermission}
                        folderTree={props.folderTree}
                        items={state.copyMoveDialogItems}
                        onOk={this.handleCopyMoveDialogOk}
                        onCancel={this.handleCopyMoveDialogCancel}
                    />
                }
            </div>
        );
    }
}